import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Footer from "./Footer";
import Header from "./Header";
import Stepper from "./Stepper";
import SkillTooltip from "./SkillTooltip";
import { useNavigate } from "react-router-dom";
import { useFormData } from "@/stores/formStore";
import { usePageMeta, PageMetaProvider } from "@/context/PageMetaContext";
import { getEraConfig } from "@/config/era-registry";
import { getFlowSteps } from "@/config/flow-steps";
import { toast } from "sonner";
import hero1920s from "@/assets/hero-1920s.webp";
import heroGaslight from "@/assets/hero-gaslight.webp";
import heroModern from "@/assets/hero-modern.webp";
import heroRegency from "@/assets/hero-regency.webp";
import heroWestern from "@/assets/hero-western.webp";
import heroDarkages from "@/assets/hero-darkages.webp";
import heroInvictus from "@/assets/era-invictus-forum.webp";
import heroPulp from "@/assets/hero-pulp.webp";
import BokehBackground from "./BokehBackground";
import SpotlightBackground from "./SpotlightBackground";
import RainBackground from "./RainBackground";
import CandlelightBackground from "./CandlelightBackground";
import DustBackground from "./DustBackground";
import SmokeBackground from "./SmokeBackground";
import ColumnsBackground from "./ColumnsBackground";
import TorchBackground from "./TorchBackground";
import FeedbackTab from "./FeedbackTab";

const eraHeroImages: Record<string, string> = {
  "1920s": hero1920s,
  gaslight: heroGaslight,
  modern: heroModern,
  regency: heroRegency,
  western: heroWestern,
  darkages: heroDarkages,
  invictus: heroInvictus,
  pulp: heroPulp,
};

const CharacterFlowLayoutInner: React.FC = () => {
  const navigate = useNavigate();
  const { formData } = useFormData();
  const { meta } = usePageMeta();
  const { t } = useTranslation("flow");
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const heroImage = eraHeroImages[formData.era] || hero1920s;

  // Fade in hero image once loaded — only re-triggers on era change
  const [heroLoaded, setHeroLoaded] = useState(false);
  useEffect(() => {
    setHeroLoaded(false);
    const img = new Image();
    img.src = heroImage;
    if (img.complete) {
      setHeroLoaded(true);
    } else {
      img.onload = () => setHeroLoaded(true);
    }
  }, [heroImage]);

  const handleStepChange = (page: number) => {
    const steps = getFlowSteps(!!formData.pulpMode);
    const target = steps[page - 1];
    if (!target) return;

    if (page > meta.currentPage) {
      const detailsDone = !!(formData.details.name && formData.details.age && formData.details.occupation);
      const needsDetails: typeof target.key[] = ["archetype", "talents", "backstory", "equipment", "characteristics"];
      if (needsDetails.includes(target.key) && !detailsDone) {
        toast.error(t("guards.completeDetails"));
        return;
      }
      if (target.key === "occupation" && formData.characteristics.strength <= 0) {
        toast.error(t("guards.rollCharacteristics"));
        return;
      }
      if (target.key === "skills" && Object.keys(formData.occupationSkills).length === 0) {
        toast.error(t("guards.allocateOccupationSkills"));
        return;
      }
    }

    if (target.route === "__era__") {
      navigate(getEraConfig(formData.era).route);
    } else {
      navigate(target.route);
    }
  };

  const showStepper = true;

  return (
    <div className="bg-subtle-pattern overflow-x-clip min-h-screen flex flex-col relative">
      {/* Era hero background banner — covers header + main */}
      <div
        className="absolute top-0 left-0 right-0 h-[460px] pointer-events-none z-[1]"
        style={{
          maskImage: 'linear-gradient(to bottom, black 20%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 20%, transparent 100%)',
        }}
      >
        <img
          src={heroImage}
          alt=""
          className={`w-full h-full object-cover object-center saturate-150 ${formData.era === "modern" ? "opacity-50" : "opacity-30"}`}
          width={1920}
          height={768}
        />
      </div>

      {/* Bokeh overlay for 1920s era */}
      {formData.era === "1920s" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] pointer-events-none transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        >
          <BokehBackground
            className="w-full h-full"
            count={20}
            color="rgba(255, 200, 60, 0.6)"
          />
        </div>
      )}

      {/* Spotlight overlay for Gaslight era */}
      {formData.era === "gaslight" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] pointer-events-none transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            maskImage: 'linear-gradient(to bottom, black 20%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 20%, transparent 100%)',
          }}
        >
          <SpotlightBackground
            className="w-full h-full"
            radius={320}
          >
            <img
              src={heroGaslight}
              alt=""
              className="w-full h-full object-cover object-center opacity-90 saturate-150"
              width={1920}
              height={768}
            />
          </SpotlightBackground>
        </div>
      )}

      {/* Rain overlay for Modern era */}
      {formData.era === "modern" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        >
          <RainBackground
            className="w-full h-full"
            dropCount={220}
            lightning={true}
            color="rgba(140, 200, 255, 0.5)"
            deflectRadius={100}
            deflectStrength={0.45}
          />
        </div>
      )}

      {/* Candlelight overlay for Regency era */}
      {formData.era === "regency" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] pointer-events-none transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        >
          <CandlelightBackground
            className="w-full h-full"
            count={12}
          />
        </div>
      )}

      {/* Dust mote overlay for Western era */}
      {formData.era === "western" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] pointer-events-none transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        >
          <DustBackground
            className="w-full h-full"
            count={70}
          />
        </div>
      )}

      {/* Hearth smoke overlay for Dark Ages era */}
      {formData.era === "darkages" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] pointer-events-none transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
        >
          <SmokeBackground className="w-full h-full" count={16} />
        </div>
      )}

      {/* Forum light-shafts & golden motes overlay for Cthulhu Invictus era */}
      {formData.era === "invictus" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] pointer-events-none transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        >
          <ColumnsBackground className="w-full h-full" moteCount={32} shaftCount={5} />
        </div>
      )}

      {/* Torch overlay for Pulp 1930s era — hover to extinguish */}
      {formData.era === "pulp" && (
        <div
          className={`absolute top-0 left-0 right-0 h-[460px] z-[2] transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        >
          <TorchBackground className="w-full h-full" />
        </div>
      )}

      <div className="relative z-10">
        <Header />
      </div>

      <main className="flex w-full flex-col items-center px-6 max-md:max-w-full max-md:px-5 flex-grow relative justify-center pt-10 pb-10 z-[3]">
        <div className="relative z-10 flex flex-col items-center w-full flex-grow">
          {meta.headerContent && (
            <div className="mb-8">
              {meta.headerContent}
            </div>
          )}

          <div className="text-center mb-2">
            {meta.titleTooltip ? (
              <SkillTooltip content={meta.titleTooltip}>
                <h1 className="text-4xl font-bold text-white mb-2 font-antiqua cursor-help">{meta.title}</h1>
              </SkillTooltip>
            ) : (
              <h1 className="text-4xl font-bold text-white mb-2 font-antiqua">{meta.title}</h1>
            )}
            <p className="text-xl text-foreground/80 font-antiqua">{meta.subtitle}</p>
          </div>

          <Outlet />

          {showStepper && (
            <Stepper currentStep={meta.currentPage} totalSteps={meta.totalPages} onStepChange={handleStepChange} />
          )}
        </div>
      </main>

      <FeedbackTab externalOpen={feedbackOpen} onExternalOpenChange={setFeedbackOpen} />
      <Footer onFeedbackOpen={() => setFeedbackOpen(true)} />
    </div>
  );
};

const CharacterFlowLayout: React.FC = () => (
  <PageMetaProvider>
    <CharacterFlowLayoutInner />
  </PageMetaProvider>
);

export default CharacterFlowLayout;
